import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  DownloadCloud,
  Loader2,
  AlertCircle,
  ListX,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { importEmployeeCourseAccessFromExcel } from "../../../api/elearning/courses/employeeCourseAccess";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { downloadCourseAssignImportTemplate } from "./courseAssignImportTemplateXlsx";

/**
 * Nhập Excel gán khóa học hàng loạt cho nhân viên trong một chi nhánh.
 */
const CourseAssignImportModal = ({
  isOpen,
  onClose,
  onSuccess,
  branchId,
  branchName,
}) => {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("merge");
  const [loading, setLoading] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const reset = () => {
    setFile(null);
    setMode("merge");
    setLoading(false);
    setImportReport(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose?.();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const name = selected.name.toLowerCase();
    const ok =
      name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv");
    if (!ok) {
      showErrorToast("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
      return;
    }
    setFile(selected);
    setImportReport(null);
  };

  const handleSubmit = async () => {
    if (!branchId) {
      showErrorToast("Thiếu chi nhánh");
      return;
    }
    if (!file) {
      showErrorToast("Vui lòng chọn file để nhập");
      return;
    }
    setImportReport(null);
    setLoading(true);
    try {
      const data = await importEmployeeCourseAccessFromExcel(file, {
        branchId,
        mode,
      });
      const updated = Number(data?.updated) || 0;
      const errors = Array.isArray(data?.errors) ? data.errors : [];
      const summaryMsg = typeof data?.message === "string" ? data.message : "";

      setImportReport({ updated, errors, message: summaryMsg });

      const hasErrors = errors.length > 0;
      const fullSuccess = updated > 0 && !hasErrors;

      if (updated > 0) onSuccess?.();

      if (fullSuccess) {
        showSuccessToast(summaryMsg || `Đã cập nhật ${updated} nhân viên.`);
        setLoading(false);
        reset();
        onClose?.();
        return;
      }

      if (updated > 0 && hasErrors) {
        showSuccessToast(
          `Đã cập nhật ${updated} nhân viên. Còn ${errors.length} dòng lỗi — xem chi tiết bên dưới.`
        );
      } else if (hasErrors) {
        showErrorToast(
          summaryMsg ||
            `Không cập nhật được dòng nào. Có ${errors.length} lỗi — xem chi tiết bên dưới.`
        );
      } else if (summaryMsg) {
        showSuccessToast(summaryMsg);
      }
    } catch (err) {
      const apiMsg = err.response?.data?.message || err.response?.data?.error;
      const text =
        apiMsg || "Không thể nhập file. Kiểm tra định dạng và dữ liệu.";
      showErrorToast(text);
      setImportReport({
        updated: 0,
        errors: [{ line: "—", message: text }],
        message: "",
      });
    } finally {
      setLoading(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            className="relative w-full max-w-xl max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-start bg-gray-50/80">
              <div>
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 uppercase tracking-tight">
                  <UploadCloud className="text-red-600 shrink-0" size={24} />
                  Nhập Excel gán khóa học
                </h2>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  Chi nhánh:{" "}
                  <span className="font-bold text-gray-700">
                    {branchName || "—"}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-200 text-gray-400 transition-all disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
              <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-4 space-y-2">
                <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">
                  Chế độ nhập
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    {
                      value: "merge",
                      label: "Cộng thêm khóa",
                      hint: "Giữ quyền cũ, chỉ thêm mã trong file",
                    },
                    {
                      value: "replace",
                      label: "Thay thế toàn bộ",
                      hint: "Xóa quyền khóa hiện có rồi gán đúng file",
                    },
                  ].map((opt) => {
                    const active = mode === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={loading}
                        onClick={() => setMode(opt.value)}
                        className={`rounded-xl border px-3 py-2 text-left transition-all ${
                          active
                            ? "border-red-200 bg-red-50 text-red-800"
                            : "border-gray-200 bg-white text-gray-600 hover:border-red-200"
                        }`}
                      >
                        <span className="block text-xs font-bold">{opt.label}</span>
                        <span className="block text-[10px] mt-0.5 opacity-80">
                          {opt.hint}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 hover:border-red-300 hover:bg-red-50/20 transition-all relative text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <div className="w-14 h-14 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <FileSpreadsheet size={28} />
                </div>
                <p className="text-sm font-bold text-gray-800">
                  {file ? file.name : "Chọn hoặc kéo thả file vào đây"}
                </p>
                <p className="text-[11px] text-gray-400 mt-1 font-medium">
                  Khuyến nghị .xlsx (file mẫu). Vẫn hỗ trợ .xls, .csv
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  downloadCourseAssignImportTemplate().catch(() => {
                    showErrorToast("Không tạo được file mẫu Excel.");
                  });
                }}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <DownloadCloud size={18} className="text-red-600" />
                Tải file mẫu Excel
              </button>

              {importReport && (
                <div
                  className={`rounded-2xl border p-4 ${
                    importReport.errors?.length
                      ? "border-amber-200 bg-amber-50/60"
                      : "border-emerald-200 bg-emerald-50/60"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {importReport.errors?.length ? (
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    ) : (
                      <ListX className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-gray-800">
                        {importReport.message ||
                          `Đã cập nhật ${importReport.updated || 0} nhân viên.`}
                      </p>
                      {importReport.errors?.length > 0 && (
                        <ul className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs text-gray-600">
                          {importReport.errors.map((err, idx) => (
                            <li key={`${err.line}-${idx}`}>
                              Dòng {err.line}: {err.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-gray-100 bg-gray-50/80 flex justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2.5 rounded-xl text-sm font-bold text-gray-600 hover:bg-white border border-transparent hover:border-gray-200 disabled:opacity-50"
              >
                Đóng
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !file}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-red-700 hover:bg-red-800 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UploadCloud className="w-4 h-4" />
                )}
                Nhập file
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CourseAssignImportModal;
